/**
 * @file page-publish-errors.ts
 * @description Отказы публикации страниц словами владельца (phase19.16 Р3а;
 * SB126–SB128; контракт docs/api/site-pages.md, коды — shared
 * `SITE_PAGE_ERROR_CODES`).
 *
 *  - 409 `site_page_draft_changed` → кто и когда изменил черновик:
 *    «Пока окно было открыто, страницу изменил Иван (14:40). Посмотрите
 *    правки и опубликуйте снова.» (uiux-r3 §3.1);
 *  - 409 `site_page_live_slug_taken` / `site_page_restore_slug_taken` → какой
 *    адрес занят (восстановление просит новый);
 *  - 409 `site_page_publish_blocked` → ошибки стоп-листа (SB127, галочка SB125).
 *
 * Тексты — реестром «код → фраза» (OCP): новый код — строка в
 * `PAGE_PUBLISH_ERROR_TEXT`. Нет своей фразы — сообщение сервера (он пишет
 * по-русски), нет и его — запасной текст вызывающего.
 */

import { SITE_PAGE_ERROR_CODES, type SitePageDraftChangedDetails, type SitePageDraftChangedPage } from "@chaiberi/shared";

import { ApiError } from "../api-client";
import type { SitePageCheck } from "../api-site-page-publish";
import { formatEditMoment } from "../site-view/changes";

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** Устаревшая страница пачки: свежий отпечаток, кто и когда правил (shared). */
export type DraftChangedPage = SitePageDraftChangedPage;

/** 409 `site_page_draft_changed` (shared `SitePageDraftChangedDetails`): `pages` всегда заполнен. */
export interface DraftChangedInfo extends SitePageDraftChangedDetails {
  pages: DraftChangedPage[];
}

/** Проверка формы страницы из `details` (ответ сервера — `unknown` до проверки). */
function readDraftPage(raw: unknown): DraftChangedPage | null {
  if (!isObject(raw)) return null;
  const pageId = str(raw.pageId);
  const fingerprint = str(raw.fingerprint);
  if (!pageId || !fingerprint) return null;
  const by = isObject(raw.editedBy) && str(raw.editedBy.id) && str(raw.editedBy.name)
    ? { id: str(raw.editedBy.id) as string, name: str(raw.editedBy.name) as string }
    : null;
  return { pageId, fingerprint, editedBy: by, editedAt: str(raw.editedAt) };
}

/** Разбор 409 «черновик изменился»; другая ошибка — `null`. */
export function parseDraftChanged(error: unknown): DraftChangedInfo | null {
  if (!(error instanceof ApiError) || error.code !== SITE_PAGE_ERROR_CODES.draftChanged) return null;
  const first = readDraftPage(error.details);
  const list = isObject(error.details) && Array.isArray(error.details.pages)
    ? error.details.pages.map(readDraftPage).filter((p): p is DraftChangedPage => p !== null)
    : [];
  const pages = list.length > 0 ? list : first ? [first] : [];
  const head = first ?? pages[0];
  if (!head) return { pageId: "", fingerprint: "", editedBy: null, editedAt: null, pages: [] };
  return { ...head, pages };
}

/** «Пока окно было открыто, страницу изменил Иван (14:40). Посмотрите правки и опубликуйте снова.» */
export function draftChangedText(info: Pick<DraftChangedPage, "editedBy" | "editedAt">, title?: string): string {
  const what = title ? `страницу «${title}»` : "страницу";
  const who = info.editedBy ? ` ${info.editedBy.name}` : "";
  const when = info.editedAt ? ` (${formatEditMoment(info.editedAt)})` : "";
  const verb = info.editedBy ? "изменил(а)" : "изменили";
  return `Пока окно было открыто, ${what} ${verb}${who}${when}. Посмотрите правки и опубликуйте снова.`;
}

/** Адрес занят другой страницей (публикация, возврат, восстановление). */
export interface SlugTaken {
  pageId: string | null;
  slug: string;
  takenByPageId: string | null;
}

/** 409 `site_page_live_slug_taken` (публикация) или `site_page_restore_slug_taken` (восстановление). */
export function parseSlugTaken(error: unknown): SlugTaken | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== SITE_PAGE_ERROR_CODES.liveSlugTaken && error.code !== "site_page_restore_slug_taken") return null;
  const d = isObject(error.details) ? error.details : {};
  return { pageId: str(d.pageId), slug: str(d.slug) ?? "", takenByPageId: str(d.takenByPageId) };
}

/** 409 `site_page_publish_blocked` → ошибки стоп-листа (SB127). */
export function parsePageChecksBlocked(error: unknown): SitePageCheck[] | null {
  if (!(error instanceof ApiError) || error.code !== "site_page_publish_blocked") return null;
  const d = isObject(error.details) ? error.details : {};
  return Array.isArray(d.checks) ? (d.checks as SitePageCheck[]) : [];
}

/** Коды, для которых у кабинета своя фраза (что делать дальше). */
export const PAGE_PUBLISH_ERROR_TEXT: Readonly<Record<string, string>> = {
  [SITE_PAGE_ERROR_CODES.nothingToPublish]: "Публиковать нечего — страницы уже на сайте в этом виде.",
  [SITE_PAGE_ERROR_CODES.publishConflict]: "Страницу только что опубликовал другой сотрудник. Обновите окно и проверьте ещё раз.",
  [SITE_PAGE_ERROR_CODES.liveSlugTaken]: "Этот адрес уже у другой страницы на сайте — поменяйте адрес в настройках страницы.",
  site_page_restore_slug_taken: "Прежний адрес страницы уже занят — укажите новый.",
  site_page_deleted: "Страницу удалили — выберите другую в списке страниц.",
  site_page_never_published: "Страница ещё не была на сайте — отменять нечего.",
  site_page_rollback_current: "Эта версия уже на сайте.",
  site_page_system_unpublish_forbidden: "Главную страницу нельзя скрыть.",
  site_page_system_undeletable: "Главную страницу нельзя удалить.",
  page_referenced: "На страницу ведёт ссылка «Условия доставки и оплаты» — скрыть или удалить её нельзя.",
  site_view_acknowledge_forbidden: "Опубликовать с незакрытыми проверками может владелец магазина или сотрудник с правом настроек магазина.",
  content_forbidden_publish: "Публикует сотрудник с правом публикации контента.",
  network_error: "Нет связи с сервером. Проверьте интернет и повторите.",
};

/** Текст ошибки для тоста или строки окна. */
export function pagePublishErrorText(error: unknown, fallback: string): string {
  const info = parseDraftChanged(error);
  if (info) return draftChangedText(info);
  if (error instanceof ApiError) return PAGE_PUBLISH_ERROR_TEXT[error.code] ?? (error.message || fallback);
  return fallback;
}
